import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import BackflowSelectDropdown from './BackflowSelectDropdown';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { SelectOption } from '../../../sharedModules/form/model';
import { BackflowJobItem } from '../../model';

interface BackflowDeviceFormProps {
  formValues: BackflowJobItem;
  setFormValues: Dispatch<SetStateAction<BackflowJobItem>>;
}

const ASSET_NO_MAX_LENGTH = 14;
const ASSET_NO_PREFIX = 'BFP-';
const TEXT_MAX_LENGTH = 50;

const BackflowDeviceForm: React.FC<BackflowDeviceFormProps> = (props) => {
  const { formValues, setFormValues } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [eqTypeOptions, setEqTypeOptions] = useState<any>();
  const [manfBfpOptions, setManfBfpOptions] = useState<SelectOption[]>();
  const [manfWmOptions, setManfWmOptions] = useState<SelectOption[]>();
  const [loading, setLoading] = useState<boolean>(true);

  const loadFormOptionsFromCache = () => {
    setLoading(true);
    try {
      // Load form options from sessionStorage (shared across all job groups)
      const cachedOptions = sessionStorage.getItem('backflowFormOptions');

      if (!cachedOptions) {
        setErrorMessage('Form options not cached. Please visit a job group page while online first.');
        setLoading(false);
        return;
      }

      const parsed = JSON.parse(cachedOptions);
      setEqTypeOptions(parsed.eqTypeOptions);
      setManfBfpOptions(parsed.manfBfpOptions);
      setManfWmOptions(parsed.manfWmOptions);
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to load form options from cache:', error);
      setErrorMessage('Failed to load form options.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFormOptionsFromCache();
  }, []); // Load once on mount

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <Grid item xs={12}>
          <TextField
            label="Asset #"
            value={formValues?.asset_no || ASSET_NO_PREFIX}
            onChange={(e) => {
              let value = e.target.value.replace(/\s/g, '');
              if (!value.startsWith(ASSET_NO_PREFIX)) value = ASSET_NO_PREFIX;
              setFormValues({ ...formValues, asset_no: value });
            }}
            inputProps={{ maxLength: ASSET_NO_MAX_LENGTH, inputMode: 'text' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <BackflowSelectDropdown
            value={formValues?.backflow_type}
            label="Equipment Type"
            onChange={(value) => setFormValues({ ...formValues, backflow_type: value })}
            options={eqTypeOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <BackflowSelectDropdown
            value={formValues?.bfp_manufacturer_id}
            label="Backflow Make"
            onChange={(value) => setFormValues({ ...formValues, bfp_manufacturer_id: value })}
            options={manfBfpOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Backflow Model #"
            value={formValues?.model_no}
            onChange={(e) => setFormValues({ ...formValues, model_no: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX_LENGTH, inputMode: 'text' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Backflow Serial #"
            value={formValues?.serial_no}
            onChange={(e) => setFormValues({ ...formValues, serial_no: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX_LENGTH, inputMode: 'text' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <BackflowSelectDropdown
            value={formValues?.wm_manufacturer_id}
            label="Water Meter Make"
            onChange={(value) => setFormValues({ ...formValues, wm_manufacturer_id: value })}
            options={manfWmOptions}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Water Meter Brass Body Serial #"
            value={formValues?.water_meter_serial}
            onChange={(e) => setFormValues({ ...formValues, water_meter_serial: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX_LENGTH, inputMode: 'numeric' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Water Meter Head Serial # (Optional)"
            value={formValues?.water_meter_serial_secondary}
            onChange={(e) => setFormValues({ ...formValues, water_meter_serial_secondary: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX_LENGTH, inputMode: 'numeric' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Location"
            value={formValues?.location}
            onChange={(e) => setFormValues({ ...formValues, location: e.target.value })}
            inputProps={{ maxLength: TEXT_MAX_LENGTH, inputMode: 'text' }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
            sx={{ mb: 3 }}
          />
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default BackflowDeviceForm;
